import { useSyncExternalStore } from 'react'
import { useLocation, useParams } from 'react-router'

import { useMyWorkspaces } from './queries'

const KEY = 'convo:last-workspace'
const CHANGED = 'convo:last-workspace-changed'

/** Remember the workspace the user last had open. A convenience only. */
export function rememberWorkspace(slug: string): void {
  try {
    localStorage.setItem(KEY, slug)
  } catch {
    /* Nothing to remember it in; the fallback is the first workspace. */
  }
  // localStorage does not notify the tab that wrote it.
  window.dispatchEvent(new Event(CHANGED))
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGED, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(CHANGED, onChange)
    window.removeEventListener('storage', onChange)
  }
}

function recall(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

/**
 * The workspace the sidebar's workspace menu should describe.
 *
 * Inside a workspace that is simply the URL's. On the reseller panel, which is
 * not workspace-scoped, it is the one the reseller last had open — falling
 * back to its own workspace and then any client company — so the workspace
 * menu stays under the reseller menu instead of vanishing the moment a
 * reseller link is clicked. Anywhere else (e.g. /admin) there is none.
 */
export function useActiveWorkspaceSlug(): string | undefined {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const { pathname } = useLocation()
  const { data: workspaces } = useMyWorkspaces()
  const remembered = useSyncExternalStore(subscribe, recall, () => null)

  if (workspaceSlug) return workspaceSlug
  if (!pathname.startsWith('/reseller') || !workspaces?.length) return undefined

  return (
    workspaces.find((w) => w.slug === remembered) ??
    workspaces.find((w) => w.role !== 'reseller') ??
    workspaces[0]
  )?.slug
}
